import { useState } from "react";
import { useParams } from "react-router-dom";
import { ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import type { MessageKey } from "../../i18n/dictionaries";
import {
  useConfirmCancel,
  useEffectEndorsement,
  useEndorsements,
  usePolicy,
  useRequestCancel,
  useRequestEndorsement,
} from "./use-policy";
import {
  CANCELLATION_SUBTYPES,
  type CancellationSubtype,
  type EndorsementResult,
  type PolicyDetail,
  type PolicyStatus,
} from "./types";

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });

// The policy header status pill reads as a calm k-status — active green, a pending
// cancellation as review-blue, a cancelled policy as the ended cue.
const STATUS_VARIANT: Partial<Record<PolicyStatus, string>> = {
  ACTIVE: "active",
  CANCELLATION_REQUESTED: "review",
  CANCELLED: "ended",
};

// ENDORSE-CANCEL-F — the console policy-detail surface (opened from the household policy row).
// Renders the flat policy read and hangs two RBAC-gated action blocks off it: a non-premium
// endorsement (request → effect) and a cancellation (request → confirm, status-driven). RBAC
// is UX; the API re-enforces (application:write/issue, policy:cancel — all OPS-scope).
export function PolicyDetailPage() {
  const { id = "" } = useParams();
  const { t } = useT();
  const q = usePolicy(id);

  if (q.isLoading)
    return (
      <div className="k-loading">
        <div className="k-loading__head">
          <span className="k-loading__spinner" />
          <span className="k-loading__label">{t("pol.loading")}</span>
        </div>
      </div>
    );
  if (q.error || !q.data)
    return (
      <div className="k-note k-note--error">
        <div className="k-note__row">
          <span className="k-note__icon k-note__icon--error">
            <Icon name="alert-triangle" size={20} />
          </span>
          <p role="alert" className="k-note__desc" data-testid="policy-error">
            {q.error instanceof ProblemError ? q.error.problem.detail : t("pol.error")}
          </p>
        </div>
      </div>
    );

  const policy = q.data;
  return (
    <section className="surface policy-detail" data-testid="policy-detail">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("pol.heading")}</h1>
          <p className="surface__subtitle">
            <strong>{policy.lineCode}</strong> · {policy.carrierId} ·{" "}
            {policy.carrierPolicyNumber ?? "—"}
          </p>
          <p className="surface__subtitle">
            {t("pol.status")}: <strong data-testid="policy-status">{policy.status}</strong> ·{" "}
            {t("pol.term")} {fmt(policy.termStart)}–{fmt(policy.termEnd)}
          </p>
        </div>
        <span className={`k-status k-status--${STATUS_VARIANT[policy.status] ?? "pending"}`}>
          <span className="k-status__dot" />
          {policy.status}
        </span>
      </header>

      <EndorsementPanel policy={policy} />
      <CancellationPanel policy={policy} />
    </section>
  );
}

// The non-premium endorsement loop: request (application:write) → effect (application:issue).
// A2 — the persisted list (GET …/endorsements) is the source of truth. It used to be local
// state only, which meant a page refresh stranded a REQUESTED endorsement with no way back
// to its /effect step. `created` is still kept, but only as an optimistic hint until the
// invalidated list comes back. Only meaningful on an ACTIVE policy (the API 409s else).
function EndorsementPanel({ policy }: { policy: PolicyDetail }) {
  const { t } = useT();
  const { me } = usePrincipal();
  const request = useRequestEndorsement(policy.id);
  const effect = useEffectEndorsement(policy.id);
  const list = useEndorsements(policy.id);
  const [note, setNote] = useState("");
  const [created, setCreated] = useState<EndorsementResult | null>(null);
  // The read is a BARE ARRAY. Guard the shape rather than trusting it: an envelope
  // surprise here would throw inside render and blank the whole policy page, and this
  // repo has already lost CI three times to exactly that class of assumption.
  const endorsements = Array.isArray(list.data) ? list.data : [];
  // The one endorsement still awaiting /effect (newest first, so the first hit wins).
  const pending = endorsements.find((e) => e.status === "ENDORSEMENT_REQUESTED");
  const openId = pending?.id ?? created?.endorsementId ?? null;

  if (!hasGrant(me, "application:write", "all")) return null; // no affordance without the grant
  const canEffect = hasGrant(me, "application:issue", "all");
  const err = request.error ?? effect.error;

  if (policy.status !== "ACTIVE")
    return (
      <div className="k-card policy-panel" data-testid="endorsement-panel">
        <p className="surface__section-label" style={{ marginTop: 0 }}>
          {t("pol.endorse.title")}
        </p>
        <p className="k-empty__desc" style={{ margin: 0 }} data-testid="endorse-inactive">
          {t("pol.endorse.inactive")}
        </p>
      </div>
    );

  return (
    <div className="k-card policy-panel" data-testid="endorsement-panel">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("pol.endorse.title")}
      </p>
      {!openId ? (
        <div className="policy-form">
          <label className="k-field">
            <span className="k-field__label">{t("pol.endorse.note")}</span>
            <input
              className="k-input"
              data-testid="endorse-note"
              aria-label={t("pol.endorse.note")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          {/* Premium-affecting endorsements need an accepted offer + go through the issuance
              desk — this surface handles the non-premium request → effect loop only. */}
          <button
            type="button"
            className="k-btn k-btn--primary"
            data-testid="endorse-add"
            disabled={note.trim().length === 0 || request.isPending}
            onClick={() =>
              request.mutate(
                { kind: "NON_PREMIUM_AFFECTING", requestedChange: { note: note.trim() } },
                { onSuccess: (res) => setCreated(res) },
              )
            }
          >
            <Icon name="edit" size={16} />
            {t("pol.endorse.add")}
          </button>
        </div>
      ) : (
        <div className="policy-created" data-testid="endorse-created">
          <p className="k-status k-status--review" style={{ margin: "0 0 12px" }}>
            <span className="k-status__dot" />
            {t("pol.endorse.created")}:{" "}
            <strong>{pending?.status ?? created?.status ?? "ENDORSEMENT_REQUESTED"}</strong>
          </p>
          {canEffect ? (
            <button
              type="button"
              className="k-btn k-btn--secondary"
              data-testid="endorse-effect"
              disabled={effect.isPending || effect.isSuccess}
              onClick={() => effect.mutate(openId)}
            >
              <Icon name="check-circle" size={16} />
              {effect.isSuccess ? t("pol.endorse.effected") : t("pol.endorse.effect")}
            </button>
          ) : (
            <p className="k-empty__desc" style={{ margin: 0 }} data-testid="endorse-no-effect">
              {t("pol.endorse.noEffect")}
            </p>
          )}
        </div>
      )}
      {err instanceof ProblemError && (
        <p role="alert" className="k-field__error" data-testid="endorse-error">
          {err.problem.detail}
        </p>
      )}

      {/* A2 — the persisted history. Present even when empty is FALSE: we render nothing
          rather than an empty shell, so the panel stays quiet on a policy with no changes. */}
      {endorsements.length > 0 && (
        <ul className="policy-endorsements" data-testid="endorse-list">
          {endorsements.map((e) => (
            <li key={e.id} className="policy-endorsements__row" data-testid={`endorse-${e.id}`}>
              <span className="policy-endorsements__kind">{e.kind}</span>
              <span className="policy-endorsements__status">{e.status}</span>
              <span className="policy-endorsements__at">{fmt(e.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The cancellation is status-driven off the read: ACTIVE → request (subtype + reason), then
// CANCELLATION_REQUESTED → confirm (optional pro-rata return premium), then CANCELLED. A CSA
// subtype triggers four-eyes server-side; the ApiClient drives the approval transparently.
function CancellationPanel({ policy }: { policy: PolicyDetail }) {
  const { t } = useT();
  const { me } = usePrincipal();
  const request = useRequestCancel(policy.id);
  const confirm = useConfirmCancel(policy.id);
  const [subtype, setSubtype] = useState<CancellationSubtype>("CUSTOMER");
  const [reason, setReason] = useState("");
  const [returnPremium, setReturnPremium] = useState("");

  if (!hasGrant(me, "policy:cancel", "all")) return null;
  const err = request.error ?? confirm.error;

  return (
    <div className="k-card policy-panel" data-testid="cancellation-panel">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("pol.cancel.title")}
      </p>

      {policy.status === "ACTIVE" && (
        <div className="policy-form">
          <label className="k-field">
            <span className="k-field__label">{t("pol.cancel.subtype")}</span>
            <div className="k-select">
              <select
                className="k-select__native"
                data-testid="cancel-subtype"
                aria-label={t("pol.cancel.subtype")}
                value={subtype}
                onChange={(e) => setSubtype(e.target.value as CancellationSubtype)}
              >
                {CANCELLATION_SUBTYPES.map((s) => (
                  <option key={s} value={s}>
                    {t(`pol.cancel.subtype.${s}` as MessageKey)}
                  </option>
                ))}
              </select>
              <span className="k-select__chevron">
                <Icon name="chevron-down" size={16} />
              </span>
            </div>
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("pol.cancel.reason")}</span>
            <input
              className="k-input"
              data-testid="cancel-reason"
              aria-label={t("pol.cancel.reason")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="k-btn k-btn--danger"
            data-testid="cancel-request"
            disabled={reason.trim().length === 0 || request.isPending}
            onClick={() => request.mutate({ subtype, reason: reason.trim() })}
          >
            {t("pol.cancel.request")}
          </button>
        </div>
      )}

      {policy.status === "CANCELLATION_REQUESTED" && (
        <div className="policy-form" data-testid="cancel-confirm-stage">
          {/* Four-eyes / governance surfaced calmly as a review-blue line. */}
          <p className="policy-requested" data-testid="cancel-requested">
            <span className="k-status k-status--review">
              <span className="k-status__dot" />
              {t("pol.cancel.requested")}
            </span>
            {policy.cancellationSubtype ? ` · ${policy.cancellationSubtype}` : ""}
            {policy.cancellationReason ? ` · ${policy.cancellationReason}` : ""}
          </p>
          <label className="k-field">
            <span className="k-field__label">{t("pol.cancel.returnPremium")}</span>
            <input
              className="k-input"
              data-testid="cancel-return-premium"
              aria-label={t("pol.cancel.returnPremium")}
              inputMode="decimal"
              value={returnPremium}
              onChange={(e) => setReturnPremium(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="k-btn k-btn--danger"
            data-testid="cancel-confirm"
            disabled={confirm.isPending}
            onClick={() =>
              confirm.mutate(
                returnPremium.trim() ? { proRataReturnPremium: returnPremium.trim() } : {},
              )
            }
          >
            {t("pol.cancel.confirm")}
          </button>
        </div>
      )}

      {policy.status === "CANCELLED" && (
        <p className="policy-done" data-testid="cancel-done">
          <span className="k-status k-status--ended">
            <span className="k-status__dot" />
            {t("pol.cancel.done")}
          </span>
          {policy.cancelledAt ? ` · ${fmt(policy.cancelledAt)}` : ""}
          {policy.proRataReturnPremium
            ? ` · ${t("pol.cancel.returned")} ${policy.proRataReturnPremium}`
            : ""}
        </p>
      )}

      {/* On any status outside ACTIVE / CANCELLATION_REQUESTED / CANCELLED (EXPIRED,
          RENEWED) none of the branches above render, leaving an empty card with
          only its title — which reads as broken. Say plainly there is nothing to
          do here for this status. */}
      {policy.status !== "ACTIVE" &&
        policy.status !== "CANCELLATION_REQUESTED" &&
        policy.status !== "CANCELLED" && (
          <p className="muted small" data-testid="cancel-na">
            {t("pol.cancel.notApplicable")}
          </p>
        )}

      {err instanceof ProblemError && (
        <p role="alert" className="k-field__error" data-testid="cancel-error">
          {err.problem.detail}
        </p>
      )}
    </div>
  );
}
